import React, { type FC, useCallback, useEffect, useState } from 'react'
import { StreamError } from '../StreamError/StreamError'
import { useAppSelector } from 'redux-store/hooks'
import { channelSelector } from 'redux-store/channel/channel.selectors'

import { getMediaObjectParams } from 'features/viewerPage/helpFunctions/getVideoPlayerData'
import { getMediaObject } from 'api/viewer/getMediaObject'
import { Loader } from 'components/Loader/Loader'
import classNames from 'classnames'
import { StreamModalContent } from 'features/viewerPage/StreamModalContent'
import { getViewerPageUrl } from './utils'
import {
  StreamOverview,
  StreamPageTabContent,
  StreamSubmenu,
  VideoPageMeta,
  ViewerPageTitle
} from './components'

import { type ViewerParameters } from 'features/viewerPage/viewerPage.interfaces'
import { type GameWithoutLivestream } from './types'

import styles from './GameWithoutLiveStream.module.scss'

type GameWithoutLiveStreamProps = {
  viewerParams?: ViewerParameters
}

export const GameWithoutLiveStream: FC<GameWithoutLiveStreamProps> = ({
  viewerParams
}) => {
  const [mediaObject, setMediaObject] = useState<
    GameWithoutLivestream | undefined
  >(undefined)
  const channel = useAppSelector(channelSelector)
  const rootDomain =
    channel?.data?.root_domain || channel?.data?.root_channel?.root_domain
  const viewerPageUrl = mediaObject && getViewerPageUrl(mediaObject, rootDomain)
  const [apiStatus, setApiStatus] = useState<
    'loading' | 'error' | 'success' | 'idle'
  >('loading')

  const getVideoPlayerData = useCallback(async () => {
    try {
      if (viewerParams) {
        setApiStatus('loading')
        const mediaObjectParams = getMediaObjectParams(viewerParams)
        const mediaObjectData = await getMediaObject(mediaObjectParams)
        setMediaObject(mediaObjectData)
        setApiStatus('success')
      }
    } catch {
      console.error('Error fetching media object data on GameWithoutLiveStream')
      setApiStatus('error')
    }
  }, [viewerParams])

  useEffect(() => {
    getVideoPlayerData()
  }, [getVideoPlayerData])

  if (apiStatus === 'loading') {
    return (
      <div className={styles.LoadingState}>
        <Loader />
      </div>
    )
  }

  if (apiStatus === 'error') {
    return (
      <div>
        <StreamError homePage={`/${channel?.data.subdomain}` || '/'} />
      </div>
    )
  }

  if (apiStatus === 'success' && mediaObject) {
    return (
      <div className={classNames(styles.StreamPage)}>
        <VideoPageMeta mediaObject={mediaObject} />
        <ViewerPageTitle
          title={mediaObject.title || undefined}
          rootDomain={rootDomain}
          viewerPageUrl={viewerPageUrl}
        />
        <div>
          <StreamOverview mediaObject={mediaObject} />
          <StreamSubmenu mediaObject={mediaObject} />
          <StreamPageTabContent mediaObject={mediaObject} />
        </div>
        <StreamModalContent />
      </div>
    )
  }

  return <></>
}
